// The frontend never shows a raw tool name: the backend sends a neutral capability label and the
// signed source record keeps the raw name for provenance. Fail closed: anything that is neither a
// label nor one of Traceira's own collectors reads as the generic platform collector.
const EXACT: Record<string, string> = {
  'http-header-inspector': 'Web probe',
  'port-banner-scanner': 'Port scanner',
  'crt.sh': 'Certificate transparency',
  'ct.subdomains': 'Certificate transparency',
  'cymru-whois': 'Ownership lookup',
  'tcp-connect': 'Network observation',
  'endpoint-service-discovery': 'Service discovery',
  'service-fingerprint-probe': 'Service discovery',
  'passive-proxy': 'Passive observation proxy',
};
const LABELS = new Set([
  ...Object.values(EXACT),
  'Vulnerability scanner',
  'Crawler',
  'Subdomain discovery',
  'TLS inspector',
  'DNS resolver',
  'Secret scanner',
]);

export function collectorLabel(sourceSystem?: string | null): string {
  const value = (sourceSystem ?? '').trim();
  if (LABELS.has(value)) return value;
  const key = value.toLowerCase();
  if (!key) return 'Traceira collector';
  if (EXACT[key]) return EXACT[key];
  // Traceira's own source taxonomy (dns.*, net.*, tls-*) keeps a meaningful capability label.
  if (key.includes('dns')) return 'DNS resolver';
  if (key.startsWith('net.')) return 'Network observation';
  if (key.startsWith('tls')) return 'TLS inspector';
  return 'Traceira collector';
}

// A version shows only as a plain release number: the runtime records a catalog profile id there,
// which names the tool. Anything else is dropped (fail closed).
const PLAIN_VERSION = /^v?\d+(?:\.\d+){0,3}$/;

/** "Crawler · 1.0", or the label alone when the version is not a plain release number. */
export function collectorText(sourceSystem?: string | null, sourceVersion?: string | null): string {
  const version = sourceVersion?.trim() ?? '';
  const label = collectorLabel(sourceSystem);
  return PLAIN_VERSION.test(version) ? `${label} · ${version}` : label;
}

// Scanner records point into tool output as "<tool>:<index>"; show "record:3". Protocol
// prefixes (dns:soa:…), JSON and line paths, URLs and host:port stay as-is.
const TOOL_PREFIX =
  /^(?!(?:dns|tcp|udp|tls|http|https|net|ct|asn|urn|record):)[a-z][a-z-]*:(?!\/\/)/i;
export function neutralPointer(pointer: string): string {
  return pointer.replace(TOOL_PREFIX, 'record:');
}
